import clsx from 'clsx';
import OinqSuccessRight from 'components/assets/oinq/oinq_success_shadow_right.svg';
import A from 'components/atoms/A/A';
import Alert from 'components/atoms/Alert/Alert';
import Button from 'components/atoms/Button/Button';
import LoadingOverlay from 'components/atoms/LoadingOverlay/LoadingOverlay';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import PasswordInput, {
  PASSWORD_REQUIREMENT_DESCRIPTIONS,
} from 'components/atoms/TextInput/PasswordInput';
import UsernameInput from 'components/atoms/TextInput/UsernameInput';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
// import SocialSignUpButtons from 'components/organisms/RegistrationForm/SocialSignUpButtons';
import { LOGIN, PRIVACY_PROMISE, TERMS_AND_CONDITIONS } from 'components/pages/pages';
import { RegistrationData } from 'components/templates/AuthenticationProvider/authApiUtils';
import { useSignUp } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { SecondFactorRequiredError } from 'components/templates/AuthenticationProvider/SecondFactorRequiredError';
import { LoginError } from 'components/templates/AuthenticationProvider/Types';
import { ConversionEvent } from 'components/templates/ThirdPartyTrackingProvider/trackingScriptEvents';
import { useConversionEventTrigger } from 'components/templates/ThirdPartyTrackingProvider/useConversionEvent';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import usePromiseState from 'hooks/usePromiseState';
import useRecaptcha, { RecaptchaProvider } from 'hooks/useRecaptcha';
import useUserSourceData from 'hooks/useUserSourceData';
import router, { useRouter } from 'next/router';
import React, { useState } from 'react';
import { isValidEmail } from 'utils/formHelper';
import classes from './RegistrationForm.qmee.module.scss';

function getSignUpErrorMap(
  email: string,
  pwd: string,
): Record<string, { title: string; message: string; action?: React.ReactNode }> {
  return {
    'Email - Already used. ': {
      title: 'Account already exists',
      message: 'The email has already been taken.',
      action: (
        <Button
          buttonType="text"
          className={classes.errorAlertAction}
          onClick={() => {
            sessionStorage.setItem('login_prefill', JSON.stringify({ email, password: pwd }));
            router.push(LOGIN.path);
          }}
        >
          <Txt>
            <strong>Click here</strong> if you would like to <strong>log in</strong>
          </Txt>
        </Button>
      ),
    },
    unsupported_country: {
      title: 'Country not available',
      message: 'Sorry. Our platform is not yet available in this country.',
    },
  };
}
export type Props = {};

export type RegistrationFormData = {
  username: string;
  uid: string;
  provider: string;
  socialToken: string;
  password: string;
  social: {
    name: string;
    firstName: string;
    lastName: string;
  };
  terms: boolean;
  termsVersion: string;
  secondFactorCode?: string;
  sessionId?: string;
  overrideCountry?: string;
  turnstileToken?: string;
};

function RegistrationForm(_props: Props) {
  const router = useRouter();
  const [signUp, { isLoading }] = usePromiseState(useSignUp());
  const [error, setError] = useState<LoginError | null>(null);
  const { loadRecaptcha, isLoadingRecaptcha } = useRecaptcha('registration');
  const { contactEmail, socialToken } = useUserSourceData();
  const { isReady, query } = router;
  const [requiresSecondFactor, setRequiresSecondFactor] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [submittedCredentials, setSubmittedCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const { startCountdown } = useCountdownTimer('secondFactorCountdown');
  const triggerConversionEvent = useConversionEventTrigger();

  // JoinApi uses the contactEmail as does KPP social flow, so check for absense of socialToken.
  const isJoinApi = contactEmail && isValidEmail(contactEmail) && !socialToken;

  const handleSignUp = async (formData: FormData) => {
    const recaptchaData = await loadRecaptcha();

    const registrationData: RegistrationData = {
      username: formData.get('username') as string,
      password: formData.get('password') as string,
      terms: true,
      termsVersion: '1.0',
    };

    try {
      const loginResult = await signUp(registrationData, recaptchaData);
      // Qmee doesn't have a confirmation step so trigger the login event here.
      if (loginResult?.guid) {
        triggerConversionEvent(ConversionEvent.CONFIRMED_REGISTRATION, {
          userGuid: loginResult.guid,
        });
      }
    } catch (err: any) {
      if (err instanceof SecondFactorRequiredError) {
        startCountdown();
        await router.push(
          {
            pathname: '/requires-confirmation',
            query: {
              sessionId: err.getSessionId(),
              username: formData.get('username')?.toString() ?? '',
              ...router.query,
            },
          },
          '/requires-confirmation',
        );
      }
      // A 'LoginError' may not be an error in the traditional sense, but rather a notification that the user must complete the DOI process:
      else if (err instanceof LoginError && err.payload && err.payload.requiresEmailConfirmation) {
        await router.push(
          {
            pathname: '/requires-email-confirmation',
            query: { email: formData.get('username')?.toString() ?? '', ...router.query },
          },
          '/requires-email-confirmation',
        );
      } else {
        setSubmittedCredentials({
          email: registrationData.username,
          password: registrationData.password,
        });
        setError(err);
      }
    }
  };

  const signUpErrorEntry = error
    ? getSignUpErrorMap(submittedCredentials?.email ?? '', submittedCredentials?.password ?? '')[
        error.loginError
      ]
    : undefined;

  return (
    <>
      {(isLoading || isLoadingRecaptcha || !isReady) && <LoadingOverlay />}
      <div className={classes.container}>
        <div className={classes.root}>
          <OinqSuccessRight className={classes.oinqWelcome} />
          <RegistrationCard
            narrowContainer
            hasCenteredTitle={true}
            hasUppercaseTitle={true}
            title={'Create your account'}
          >
            <Txt component="p" className={classes.formIntro}>
              and start earning!
            </Txt>

            {error ? (
              <Alert
                type="error"
                title={signUpErrorEntry?.title ?? 'An Error Occurred'}
                subtitle={signUpErrorEntry?.message ?? error.message}
                action={signUpErrorEntry?.action}
                className={classes.errorAlert}
                onClose={() => setError(null)}
              />
            ) : undefined}

            <Form
              submitDisabled={
                isLoading || isLoadingRecaptcha
                // || (Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) && !turnstileToken)
              }
              submitName="Sign me up!"
              buttonWrapperClassName={classes.buttonWrapper}
              onSubmit={handleSignUp}
              submitButtonType="primary"
              className={classes.formOverrides}
              submitButtonClassName={classes.submitButton}
            >
              {!requiresSecondFactor && (
                <>
                  <UsernameInput
                    overrideTitle="Email"
                    defaultValue={typeof query.email === 'string' ? query.email : undefined}
                  />

                  <PasswordInput
                    id={'password'}
                    title={'Password'}
                    placeholder={'Enter password'}
                    required
                    autoComplete={'current-password'}
                    minLength={8}
                    onInput={(e) => {
                      setPassword(e.currentTarget.value);
                    }}
                    overrideErrorMessage={(_validity, _value) => {
                      return PASSWORD_REQUIREMENT_DESCRIPTIONS;
                    }}
                  />

                  <p className={classes.passwordRequirementText}>
                    Password must have at least 8 characters{' '}
                    <span
                      className={clsx(
                        classes.passwordLengthIndicator,
                        password.length >= 8
                          ? classes.passwordLengthValid
                          : classes.passwordLengthInvalid,
                      )}
                    >
                      {Math.min(password.length, 8)}/8
                    </span>
                  </p>

                  <p className={classes.termsText}>
                    By signing up you agree to the{' '}
                    <A target="_blank" href={TERMS_AND_CONDITIONS.path}>
                      Terms of Service
                    </A>{' '}
                    and{' '}
                    <A target="_blank" href={PRIVACY_PROMISE.path}>
                      Privacy Policy
                    </A>
                  </p>
                </>
              )}
            </Form>

            {/* Temporarily removed until we get social sign in working */}
            {/* <SocialSignUpButtons
              className={classes.socialSignUpButtons}
              compactButtons
              hasTitle
              title="or continue via"
            /> */}
          </RegistrationCard>
        </div>
      </div>
    </>
  );
}

export default function RegistrationFormWrapper(props: Props) {
  return (
    <RecaptchaProvider>
      <RegistrationForm {...props} />
    </RecaptchaProvider>
  );
}
